import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  alpha,
} from '@mui/material';
import TrendingUp from '@mui/icons-material/TrendingUp';
import PublishedWithChanges from '@mui/icons-material/PublishedWithChanges';
import Insights from '@mui/icons-material/Insights';
import ShowChart from '@mui/icons-material/ShowChart';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import moment from 'moment';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RcTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  Line,
  ReferenceLine,
} from 'recharts';
import { getPricingDashboard } from '../../services/api';
import {
  DYNAMIC_PRICING_ACCENT,
  getDynamicPricingMetricCardSx,
  getDynamicPricingPanelSx,
  getDynamicPricingSectionHeaderSubtitleSx,
  getDynamicPricingSectionHeaderTitleSx,
  getDynamicPricingThemeColors,
} from '../../utils/dynamicPricingTheme';
import {
  getDynamicPricingChartsGridSx,
  getDynamicPricingKpiGridSx,
  isDynamicPricingTabletLayout,
} from '../../utils/dynamicPricingLayout';

const PIE_COLORS = {
  SUCCESS: DYNAMIC_PRICING_ACCENT,
  PARTIAL: '#ffb74d',
  FAILED: '#ef5350',
  PENDING: '#90a4ae',
  UNKNOWN: '#78909c',
};

function formatPct(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function KpiCard({ title, value, subtitle, icon: Icon, isDark, accent, themeColors, chip }) {
  return (
    <Card
      elevation={0}
      sx={{
        width: '100%',
        ...getDynamicPricingMetricCardSx(isDark, accent, themeColors),
      }}
    >
      <CardContent
        sx={{
          pt: 2.5,
          pb: 2,
          px: 2.5,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          '&:last-child': { pb: 2 },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1.5}
          sx={{ width: '100%', flex: 1, minHeight: 0 }}
        >
          <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.04} component="div">
              {title}
            </Typography>
            <Typography
              variant="h4"
              component="div"
              sx={{
                fontWeight: 800,
                mt: 0.5,
                lineHeight: 1.2,
                letterSpacing: -0.5,
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ mt: 0.75, lineHeight: 1.45 }}
              >
                {subtitle}
              </Typography>
            )}
            {chip ? <Box sx={{ mt: 1 }}>{chip}</Box> : null}
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              alignSelf: 'flex-start',
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isDark ? alpha(accent, 0.15) : alpha(accent, 0.2),
              color: accent,
            }}
          >
            {Icon ? <Icon sx={{ fontSize: 26 }} /> : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function DynamicPricingDashboard({ isDark, accentColor, themeColors: themeColorsProp, notifyError }) {
  const themeColors = themeColorsProp || getDynamicPricingThemeColors(isDark);
  const isTablet = isDynamicPricingTabletLayout();
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPricingDashboard({ days });
      setData(res);
    } catch (err) {
      notifyError(err?.error || err?.message || 'Failed to load pricing dashboard');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, notifyError]);

  useEffect(() => {
    void load();
  }, [load]);

  const chartDaily = useMemo(() => {
    const rows = Array.isArray(data?.daily) ? data.daily : [];
    return rows.map((d) => ({
      ...d,
      label: moment(d.date).format('MMM D'),
      avgPct: d.avg_percent_change != null ? Number(d.avg_percent_change) : null,
    }));
  }, [data]);

  const pieData = useMemo(() => {
    const raw = data?.publish_by_status || {};
    return Object.entries(raw)
      .map(([name, value]) => ({
        name,
        value: Number(value) || 0,
      }))
      .filter((d) => d.value > 0);
  }, [data]);

  const summary = data?.summary || {};
  const modeled = data?.modeled_outcomes || {};

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10, width: '100%' }}>
        <CircularProgress sx={{ color: accentColor }} />
      </Box>
    );
  }

  /** One content rail: no MUI Grid negative margins — left/right edges match every section */
  const railSx = {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    mx: 0,
    px: 0,
  };

  return (
    <Stack spacing={3} sx={railSx}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) auto' },
          gap: 2,
          alignItems: 'start',
          justifyContent: 'stretch',
          ...railSx,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="h2" sx={getDynamicPricingSectionHeaderTitleSx(isDark)}>
            Dynamic pricing insights
          </Typography>
          <Typography variant="body2" sx={{ ...getDynamicPricingSectionHeaderSubtitleSx(isDark), mt: 0.5 }}>
            Activity from rate updates and publish batches. Collection and booking lift use a transparent elasticity sketch when
            PMS pickup is not linked—see the modeled card below.
          </Typography>
        </Box>
        <Box
          sx={{
            justifySelf: { xs: 'stretch', sm: 'end' },
            width: { xs: '100%', sm: 'auto' },
            minWidth: 0,
          }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={days}
            onChange={(_, v) => v != null && setDays(v)}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              display: 'flex',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
              '& .MuiToggleButton-root': {
                px: { xs: 1.25, sm: 1.75 },
                fontWeight: 600,
                textTransform: 'none',
                borderColor: 'divider',
                flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
                '&.Mui-selected': {
                  bgcolor: alpha(accentColor, isDark ? 0.2 : 0.25),
                  color: isDark ? accentColor : '#0d3d32',
                  borderColor: alpha(accentColor, 0.5),
                },
              },
            }}
          >
            <ToggleButton value={30}>30d</ToggleButton>
            <ToggleButton value={90}>90d</ToggleButton>
            <ToggleButton value={180}>180d</ToggleButton>
            <ToggleButton value={365}>1y</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {loading && data && (
        <Box sx={{ width: '100%', ...railSx }}>
          <LinearProgressThin accentColor={accentColor} />
        </Box>
      )}

      <Box sx={{ ...getDynamicPricingKpiGridSx(isTablet), ...railSx }}>
        <Box sx={{ minWidth: 0, display: 'flex', boxSizing: 'border-box' }}>
          <KpiCard
            title="Rate updates"
            value={summary.rate_change_events ?? 0}
            subtitle="Logged price changes in this window"
            icon={PublishedWithChanges}
            isDark={isDark}
            accent={accentColor}
            themeColors={themeColors}
          />
        </Box>
        <Box sx={{ minWidth: 0, display: 'flex', boxSizing: 'border-box' }}>
          <KpiCard
            title="Net price direction"
            value={`${summary.price_increases ?? 0}↑ / ${summary.price_decreases ?? 0}↓`}
            subtitle="Where prior rate was known"
            icon={ShowChart}
            isDark={isDark}
            accent={accentColor}
            themeColors={themeColors}
            chip={
              summary.avg_percent_change_when_prior_known != null ? (
                <Chip
                  size="small"
                  sx={{ mt: 1, fontWeight: 700, bgcolor: alpha(accentColor, 0.15) }}
                  label={`Avg Δ ${formatPct(summary.avg_percent_change_when_prior_known)}`}
                />
              ) : null
            }
          />
        </Box>
        <Box sx={{ minWidth: 0, display: 'flex', boxSizing: 'border-box' }}>
          <KpiCard
            title="Publish success"
            value={
              summary.publish_success_rate != null
                ? `${(Number(summary.publish_success_rate) * 100).toFixed(0)}%`
                : '—'
            }
            subtitle={`${summary.rates_publish_succeeded ?? 0} ok · ${summary.rates_publish_failed ?? 0} failed`}
            icon={TrendingUp}
            isDark={isDark}
            accent={accentColor}
            themeColors={themeColors}
          />
        </Box>
        <Box sx={{ minWidth: 0, display: 'flex', boxSizing: 'border-box' }}>
          <KpiCard
            title="Modeled collection Δ"
            value={formatPct(modeled.implied_collection_change_pct)}
            subtitle="From avg rate move × elasticity (planning only)"
            icon={Insights}
            isDark={isDark}
            accent={accentColor}
            themeColors={themeColors}
            chip={
              modeled.implied_booking_volume_change_pct != null ? (
                <Chip
                  size="small"
                  sx={{ mt: 1, fontWeight: 700 }}
                  label={`Vol ~ ${formatPct(modeled.implied_booking_volume_change_pct)}`}
                  variant="outlined"
                />
              ) : null
            }
          />
        </Box>
      </Box>

      <Alert severity="info" icon={<InfoOutlined />} sx={{ borderRadius: 2, width: '100%', boxSizing: 'border-box' }}>
        <Typography variant="body2" component="span" sx={{ display: 'block', lineHeight: 1.55 }}>
          {modeled.disclaimer ||
            'Connect PMS booking and revenue feeds to replace modeled lift with verified pickup after each publish.'}
        </Typography>
      </Alert>

      <Box sx={{ ...getDynamicPricingChartsGridSx(isTablet) }}>
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              ...getDynamicPricingPanelSx(isDark, accentColor),
            }}
          >
            <Typography variant="overline" fontWeight={800} sx={{ color: accentColor, letterSpacing: 0.12 }}>
              Activity & direction
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
              Daily rate events (area) and increases vs decreases (bars)
            </Typography>
            <Box sx={{ width: '100%', flex: 1, minHeight: isTablet ? 340 : 320, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%" minHeight={isTablet ? 340 : 320}>
                <ComposedChart data={chartDaily} margin={{ top: 12, right: 8, left: 0, bottom: 28 }}>
                  <defs>
                    <linearGradient id="dpAreaUpdates" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={accentColor} stopOpacity={0.45} />
                      <stop offset="95%" stopColor={accentColor} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#33333d' : '#ececf2'} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={32} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={44} allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={48} allowDecimals={false} />
                  <RcTooltip
                    contentStyle={{
                      borderRadius: 12,
                      border: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
                      background: isDark ? '#1e1e24' : '#fff',
                    }}
                  />
                  <Legend wrapperStyle={{ paddingTop: 16 }} verticalAlign="bottom" align="center" />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="rate_updates"
                    name="Updates"
                    stroke={accentColor}
                    fill="url(#dpAreaUpdates)"
                    strokeWidth={2}
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="price_increases"
                    name="Increases"
                    fill={alpha('#4ce1b6', 0.85)}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={22}
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="price_decreases"
                    name="Decreases"
                    fill={alpha('#ff8a65', 0.9)}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={22}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              width: '100%',
              height: '100%',
              minHeight: { xs: 360, xl: 420 },
              display: 'flex',
              flexDirection: 'column',
              ...getDynamicPricingPanelSx(isDark, accentColor),
            }}
          >
            <Typography variant="overline" fontWeight={800} sx={{ color: accentColor, letterSpacing: 0.12 }}>
              Publish batches
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 0.5 }}>
              Outcomes in the same period
            </Typography>
            <Box sx={{ flex: 1, minHeight: 260, minWidth: 0, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <PieChart margin={{ top: 0, right: 8, left: 8, bottom: 8 }}>
                  <Pie
                    data={pieData.length ? pieData : [{ name: 'No data', value: 1 }]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="45%"
                    innerRadius={52}
                    outerRadius={82}
                    paddingAngle={pieData.length > 1 ? 2 : 0}
                  >
                    {(pieData.length ? pieData : [{ name: 'No data', value: 1 }]).map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={PIE_COLORS[entry.name] || PIE_COLORS.UNKNOWN}
                        stroke={isDark ? '#1a1a1f' : '#fff'}
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <RcTooltip />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    layout="horizontal"
                    wrapperStyle={{ width: '100%', paddingTop: 8 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              width: '100%',
              gridColumn: { xs: '1', ...(isTablet ? { xl: '1 / -1' } : { lg: '1 / -1' }) },
              ...getDynamicPricingPanelSx(isDark, accentColor),
            }}
          >
            <Typography variant="overline" fontWeight={800} sx={{ color: accentColor, letterSpacing: 0.12 }}>
              Average % change by day
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
              When a previous rate exists; flat segments mean no qualifying updates that day
            </Typography>
            <Box sx={{ width: '100%', height: 280, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <ComposedChart data={chartDaily} margin={{ top: 12, right: 12, left: 4, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#33333d' : '#ececf2'} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={28} />
                  <YAxis tick={{ fontSize: 11 }} width={44} tickFormatter={(v) => `${v}%`} />
                  <ReferenceLine y={0} stroke={isDark ? '#555' : '#bdbdbd'} strokeDasharray="4 4" />
                  <RcTooltip
                    formatter={(val) => (val != null ? `${Number(val).toFixed(2)}%` : '—')}
                    contentStyle={{
                      borderRadius: 12,
                      border: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
                      background: isDark ? '#1e1e24' : '#fff',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="avgPct"
                    name="Avg Δ %"
                    stroke={accentColor}
                    strokeWidth={2.5}
                    dot={false}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
      </Box>
    </Stack>
  );
}

function LinearProgressThin({ accentColor }) {
  return (
    <Box
      sx={{
        height: 3,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: alpha(accentColor, 0.12),
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '40%',
          background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
          animation: 'dpShimmer 1.1s ease-in-out infinite',
          '@keyframes dpShimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(350%)' },
          },
        }}
      />
    </Box>
  );
}
